'use client';

import { Close } from '@sprd/sprd-component-kit/v2';
import { MouseEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { NonIndexableLink } from '@/components/NonIndexableLink/NonIndexableLink';
import { getPathname, useRouter } from '@/i18n/navigation';
import { mpProductGridLoadingAtom } from '@/store/atoms';
import { Locale } from '@/utility/locale';
import { getMPListPageHrefArgs } from '@/utility/urls';
import { MarketplaceSearchParams } from '../utility/validationSchema';

let hasSearchTermBeenVisible = false;

type MPSearchTermResetProps = {
  locale: Locale;
  searchTerm?: string | undefined;
  className?: string;
  departmentSlug?: string | undefined;
  categorySlugs?: string[] | undefined;
  filterParams?: MarketplaceSearchParams | undefined;
};

/**
 * The current search term with a close icon, linking to the same page without it. The target is not
 * indexable — it is the same content the plain category page already serves.
 *
 * Slides down on a genuine appearance, pushing the headline below it down as it grows
 */
export const MPSearchTermReset = ({
  locale,
  searchTerm,
  className,
  departmentSlug,
  categorySlugs,
  filterParams,
}: MPSearchTermResetProps) => {
  const t = useTranslations();
  const router = useRouter();
  const [open, setOpen] = useState(hasSearchTermBeenVisible);
  const [dismissed, setDismissed] = useState(false);
  const setProductGridLoading = useSetAtom(mpProductGridLoadingAtom);

  useEffect(() => {
    hasSearchTermBeenVisible = Boolean(searchTerm);
    if (searchTerm) {
      setDismissed(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (open || !searchTerm || dismissed) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [open, searchTerm, dismissed]);

  const handleCloseClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    setDismissed(true);
    setOpen(false);
    setProductGridLoading(true);

    router.push(
      getMPListPageHrefArgs({ departmentSlug, categorySlugs, queryParams: filterParams })
    );
  };

  if (!searchTerm) {
    return null;
  }

  const href = getPathname({
    href: getMPListPageHrefArgs({ departmentSlug, categorySlugs, queryParams: filterParams }),
    locale,
  });

  return (
    <div className={className}>
      <div
        className={clsx(
          'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0">
          <NonIndexableLink
            href={href}
            className="inline-flex max-w-full items-center gap-1 text-base"
            onClick={handleCloseClick}
          >
            <span className="truncate">&#34;{searchTerm}&#34;</span>
            <Close className="size-4 shrink-0" aria-hidden />
            <span className="sr-only">{t('general.a11y.clearSearch')}</span>
          </NonIndexableLink>
        </div>
      </div>
    </div>
  );
};
